import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, 
  DollarSign, 
  RefreshCw, 
  Activity,
  ArrowRightLeft,
  Clock,
  Zap,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminExchangeDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [livePrices, setLivePrices] = useState([]);
  const [recentExchanges, setRecentExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [showTreasuryModal, setShowTreasuryModal] = useState(false);
  const [treasuryForm, setTreasuryForm] = useState({
    currency_code: '',
    buy_rate: '',
    sell_rate: ''
  });

  useEffect(() => {
    fetchDashboard();
    fetchRecentExchanges();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboard();
      fetchRecentExchanges();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/dashboard`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      setStats(data.stats);
      setLivePrices(data.live_prices || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentExchanges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/transactions?limit=20`, {
        credentials: 'include'
      });
      const data = await response.json();
      setRecentExchanges(data.exchanges || []);
    } catch (error) {
      console.error('Failed to load exchanges:', error);
    }
  };

  const handleUpdatePrices = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/update-prices`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update prices');
      }
      
      toast.success(`Updated ${data.updated || 0} prices successfully`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleInitialize = async () => {
    if (!confirm('Initialize exchange engine with default configurations? This will create default currencies, fees, and treasury settings.')) {
      return;
    }

    setInitializing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/initialize`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to initialize');
      }
      
      toast.success('Exchange engine initialized successfully');
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInitializing(false);
    }
  };

  const handleTreasurySubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/treasury/rates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(treasuryForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update treasury rates');
      }

      toast.success('Treasury rates updated successfully');
      setShowTreasuryModal(false);
      setTreasuryForm({ currency_code: '', buy_rate: '', sell_rate: '' });
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (!num) return '0';
    return parseFloat(num).toFixed(decimals);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Exchange Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor exchange activity and manage pricing
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleUpdatePrices}
              disabled={updating}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${updating ? 'animate-spin' : ''}`} />
              {updating ? 'Updating...' : 'Update Prices'}
            </Button>
            <Button
              onClick={handleInitialize}
              disabled={initializing}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Zap className="h-4 w-4" />
              {initializing ? 'Initializing...' : 'Initialize Engine'}
            </Button>
            <Button
              onClick={() => setShowTreasuryModal(true)}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Treasury Rates
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Exchanges</p>
                      <p className="text-2xl font-bold">{stats?.total_exchanges || 0}</p>
                    </div>
                    <ArrowRightLeft className="h-8 w-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Volume (USD)</p>
                      <p className="text-2xl font-bold">
                        ${formatNumber(stats?.total_volume_usd || 0, 2)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Fees Collected (USD)</p>
                      <p className="text-2xl font-bold">
                        ${formatNumber(stats?.total_fees_usd || 0, 2)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">24h Exchanges</p>
                      <p className="text-2xl font-bold">{stats?.exchanges_24h || 0}</p>
                    </div>
                    <Activity className="h-8 w-8 text-orange-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Live Prices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Live Market Prices (USD)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {livePrices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No live prices available. Click "Update Prices" to fetch latest data.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {livePrices.map((price) => (
                      <div
                        key={price.currency_code}
                        className="p-4 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="font-bold text-lg">{price.currency_code}</div>
                        <div className="text-2xl font-mono">
                          ${formatNumber(price.price, 8)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {new Date(price.updated_at).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Exchanges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5" />
                  Recent Exchanges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentExchanges.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No exchanges yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentExchanges.map((exchange) => (
                      <div
                        key={exchange.transaction_id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {exchange.transaction_id?.slice(0, 8)}
                            </Badge>
                            <span className="font-medium">
                              {exchange.from_amount} {exchange.from_currency} → {formatNumber(exchange.to_amount_after_fees, 6)} {exchange.to_currency}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            User: {exchange.user_email} • {new Date(exchange.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">
                            Rate: {formatNumber(exchange.exchange_rate, 8)}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Fee: ${formatNumber(exchange.total_fees_usd || 0, 4)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Treasury Modal */}
        {showTreasuryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle>Update Treasury Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTreasurySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Currency Code *</Label>
                    <Input
                      value={treasuryForm.currency_code}
                      onChange={(e) =>
                        setTreasuryForm({ ...treasuryForm, currency_code: e.target.value.toUpperCase() })
                      }
                      placeholder="ZAR, USD"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Buy Rate (Platform Buys) *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={treasuryForm.buy_rate}
                      onChange={(e) =>
                        setTreasuryForm({ ...treasuryForm, buy_rate: e.target.value })
                      }
                      placeholder="18.50"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Sell Rate (Platform Sells) *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={treasuryForm.sell_rate}
                      onChange={(e) =>
                        setTreasuryForm({ ...treasuryForm, sell_rate: e.target.value })
                      }
                      placeholder="19.20"
                      required
                    />
                  </div>

                  <div className="text-xs text-muted-foreground p-3 bg-muted rounded-lg">
                    <p className="font-medium mb-1">Note:</p>
                    <p>Buy rate is what the platform pays when buying from users.</p>
                    <p>Sell rate is what the platform charges when selling to users.</p>
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1">
                      Update Rates
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowTreasuryModal(false);
                        setTreasuryForm({ currency_code: '', buy_rate: '', sell_rate: '' });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
